"use client"

import { useEffect, useState } from "react"
import { format } from "date-fns"
import { es } from "date-fns/locale"
import { Download, Eye, Filter, Search, Calendar, PawPrint, Camera, ChevronLeft, ChevronRight, X } from "lucide-react"
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useUserReports } from "@/hooks/useUserReports"
import { motion, AnimatePresence } from "framer-motion"
import { useAnalysis } from '../AnalysisContext'
import toast from 'react-hot-toast';
import { useUser } from "@clerk/clerk-react";
import UpgradeModalReport from './UpgradeModalReport';
import PDFGenerationLoader from '../components/PDFGenerationLoader';
import axios from 'axios';
import { cn } from "@/lib/utils"

const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center h-full space-y-4">
    <motion.div
      className="relative w-24 h-24"
      animate={{ rotate: 360 }}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "linear"
      }}
    >
      <div className="absolute inset-0 rounded-full border-4 border-amber-200 border-t-amber-500" />
      <div className="absolute inset-0 flex items-center justify-center">
        <PawPrint className="w-10 h-10 text-amber-600" />
      </div>
    </motion.div>
  </div>
);

export default function ReportHistorySection() {
  const { isSignedIn, isLoaded, user } = useUser();

  if (!isLoaded || !isSignedIn) {
    return <motion.div
      className="flex flex-col items-center py-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <LoadingSpinner />
    </motion.div>;
  }

  const [searchTerm, setSearchTerm] = useState("")
  const [previewImage, setPreviewImage] = useState(null)
  const { clearContext, updateAnalysis, updateImages, updateImagesContext, analysisData, userInput, imageUrls, updateBreedGuide, setPetName } = useAnalysis();
  const [isExporting, setIsExporting] = useState(false);
  const isPremium = user?.unsafeMetadata?.isPremium;

  const [showUpgradeModalReport, setShowUpgradeModalReport] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [isBreedLoading, setIsBreedLoading] = useState(false);
  const navigate = useNavigate();
  const {
    reports,
    loading,
    error,
    pagination,
    timeFilter,
    setTimeFilter,
    reportCounts,
    handlePageChange,
    groupedByPet,
    selectedPet,
    setSelectedPet
  } = useUserReports(user?.id);

  const timeFilterOptions = [
    { id: 'all', label: 'Todos', count: reportCounts.all },
    { id: 'today', label: 'Hoy', count: reportCounts.today },
    { id: 'week', label: 'Esta Semana', count: reportCounts.week },
    { id: 'month', label: 'Este Mes', count: reportCounts.month },
    { id: 'older', label: 'Anteriores', count: reportCounts.older }
  ]

  const formatCompactDate = (dateString) => {
    try {
      const date = new Date(dateString)
      return format(date, "dd/MM/yyyy HH:mm", { locale: es })
    } catch (e) {
      return 'Fecha desconocida'
    }
  }

  const handleExportPDF = async (analysisData) => {
    if (!isPremium) {
      setShowUpgradeModalReport(true);
      return;
    }
    try {
      setIsExporting(true);
      setPdfProgress(0);

      // Start progress simulation
      const progressInterval = setInterval(() => {
        setPdfProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90; // Keep at 90% until actual completion
          }
          return prev + 5;
        });
      }, 500);

      const sanitizedData = analysisData;

      const validImageUrls = imageUrls?.filter(url =>
        url && (url.startsWith('http://') || url.startsWith('https://'))
      ) || [];

      const exportData = {
        analysisData: sanitizedData,
        userId: user?.id,
        userContext: userInput || 'No se proporcionó contexto adicional',
        imageUrls: validImageUrls,
        timestamp: analysisData.timestamp || new Date().toISOString()
      };

      const response = await axios.post('/api/pdf/analysis-export', exportData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 90000
      });

      if (!(response.data instanceof Blob)) {
        throw new Error('Invalid response format');
      }

      // Set to 100% when PDF is ready
      setPdfProgress(100);
      clearInterval(progressInterval);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateStr = (analysisData.timestamp || new Date().toISOString()).split('T')[0];
      link.setAttribute('download', `analisis_dogscan_${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();

      toast.success('Reporte PDF descargado correctamente');
    } catch (error) {
      console.error('Export error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('La operación tardó demasiado tiempo. Por favor, intente nuevamente.');
      } else {
        toast.error('Error al exportar el análisis. Por favor, intente nuevamente.');
      }
    } finally {
      setIsExporting(false);
      setPdfProgress(0);
    }
  };

  const fetchBreed = async (breed, analysisType) => {

    // Validate breed input
    if (!breed?.trim()) {
      return;
    }
    if (analysisType === 'quick') {
      toast.success('Información cargada correctamente');
      return;
    }

    try {
      setIsBreedLoading(true);
      const response = await fetch(`/api/breeds/${breed}`);

      // Log response status

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.breed) {
        updateBreedGuide(result.breed.content);
        toast.success('Información cargada correctamente');
      } else {
        throw new Error('No se encontró información de la raza');
      }

    } catch (err) {
      console.error('❌ Error fetching breed:', err);
      toast.error('Error al cargar la información de la raza. Por favor, intente nuevamente.');
    } finally {
      setIsBreedLoading(false);
    }
  };

  return (
    <Card className="w-full bg-white shadow-sm min-h-screen sm:min-h-0 border-0 sm:border overflow-hidden">
      <CardContent className="p-0 sm:p-6">
        {/* Enhanced Header with Netflix-like styling */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b sm:border-none p-4 sm:p-0 sm:mb-6">
          <div className="flex flex-col space-y-4">
            {/* Title and description - YouTube style */}
            <div className="hidden sm:block">
              <h2 className="text-2xl font-bold text-amber-900 mb-1">Historial de Análisis</h2>
              <p className="text-amber-700/70 text-sm">Revisa todos tus análisis anteriores y accede a la información de tus mascotas</p>
            </div>
            
            {/* Pet tabs with enhanced scrolling - Netflix style */}
            <div className="mb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 sm:pb-4">
                <Button
                  variant={!selectedPet ? "default" : "outline"}
                  onClick={() => setSelectedPet(null)}
                  className={cn(
                    "flex items-center gap-2 flex-shrink-0 rounded-full px-4 transition-all duration-300",
                    !selectedPet 
                      ? "bg-amber-500 hover:bg-amber-600 text-white shadow-md" 
                      : "border-amber-200 hover:border-amber-300 hover:bg-amber-50/50"
                  )}
                  size="sm"
                >
                  <PawPrint className="h-4 w-4" />
                  <span>Todos</span>
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-xs",
                    !selectedPet ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"
                  )}>
                    {reportCounts.all}
                  </span>
                </Button>

                {Object.entries(groupedByPet).map(([petName, petReports]) => (
                  <Button
                    key={petName}
                    variant={selectedPet === petName ? "default" : "outline"}
                    onClick={() => setSelectedPet(petName)}
                    className={cn(
                      "flex items-center gap-2 flex-shrink-0 rounded-full px-4 transition-all duration-300",
                      selectedPet === petName 
                        ? "bg-amber-500 hover:bg-amber-600 text-white shadow-md" 
                        : "border-amber-200 hover:border-amber-300 hover:bg-amber-50/50"
                    )}
                    size="sm"
                  >
                    <div className="w-5 h-5 rounded-full overflow-hidden bg-gray-100 border border-white/80 shadow-sm">
                      <img
                        src={petReports[0]?.images?.[0]}
                        alt={petName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span>{petName}</span>
                    <span className={cn(
                      "px-2 py-0.5 rounded-full text-xs",
                      selectedPet === petName ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"
                    )}>
                      {petReports.length}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Filter bar with Canva-like styling */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <Select value={timeFilter} onValueChange={setTimeFilter}>
                <SelectTrigger className="w-full sm:w-[180px] h-10 border-amber-200 bg-amber-50/50 hover:bg-amber-50 transition-colors">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-amber-600" />
                    <SelectValue placeholder="Filtrar por fecha" />
                  </div>
                </SelectTrigger>
                <SelectContent className="border-amber-200 shadow-lg">
                  {timeFilterOptions.map(option => (
                    <SelectItem key={option.id} value={option.id} className="focus:bg-amber-50 focus:text-amber-900">
                      <div className="flex items-center justify-between w-full">
                        <span>{option.label}</span>
                        <span className="bg-amber-100/70 text-amber-700 px-2 py-0.5 rounded-full text-xs">
                          {option.count}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="hidden sm:flex items-center gap-2 text-sm text-amber-700">
                <div className="h-1 w-1 rounded-full bg-amber-400"></div>
                <span>{reports.length} análisis encontrados</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Content Area */}
        <div className="p-4 sm:p-0 sm:mt-6">
          {loading ? (
            <motion.div
              className="flex flex-col items-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LoadingSpinner />
            </motion.div>
          ) : error ? (
            <motion.div 
              className="text-center py-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-red-50 text-red-600 px-6 py-8 rounded-xl max-w-md mx-auto shadow-sm border border-red-100">
                <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PawPrint className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-medium mb-2">Ocurrió un error</h3>
                <p>{error}</p>
              </div>
            </motion.div>
          ) : reports.length === 0 ? (
            <motion.div 
              className="text-center py-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-amber-50/50 text-amber-700 px-6 py-10 rounded-xl max-w-md mx-auto shadow-sm border border-amber-100/50">
                <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PawPrint className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-lg font-medium mb-2">No hay análisis disponibles</h3>
                <p className="text-amber-600/80 mb-6">{timeFilter !== 'all' ? 'No se encontraron análisis en este período de tiempo.' : 'Aún no has realizado ningún análisis con DogScan.'}</p>
                <Button 
                  className="bg-amber-500 hover:bg-amber-600 text-white new-analysis-modal-trigger"
                  onClick={() => navigate("/dog-analyzer")}
                >
                  <Camera className="mr-2 h-4 w-4" />
                  Realizar nuevo análisis
                </Button>
              </div>
            </motion.div>
          ) : (
            <div>
              {/* Netflix-style grid layout with enhanced cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <AnimatePresence mode="sync">
                  {(selectedPet ? groupedByPet[selectedPet] : reports).map((report, index) => (
                    <motion.div
                      key={report.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="group cursor-pointer"
                      onClick={() => {
                        updateAnalysis(report.analysis, report.context);
                        updateImages(report.images);
                        fetchBreed(report.breed, report.analysisType);
                        setPetName(report.petName);
                        navigate("/overview");
                      }}
                    >
                      <Card className="overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 rounded-xl bg-white">
                        {/* Enhanced image container with overlay effects */}
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={report.images?.[0]}
                            alt={`Análisis de ${report.petName}`}
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                          />
                          
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
                          
                          {/* Floating date badge - YouTube style */}
                          <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2.5 py-1.5 rounded-full">
                            <time className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1.5 text-amber-400" />
                              {formatCompactDate(report.created_at)}
                            </time>
                          </div>
                          
                          {/* Action buttons - Netflix style */}
                          <div className="absolute bottom-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-2 group-hover:translate-y-0">
                            {/* Export button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 rounded-full bg-white/90 hover:bg-white text-amber-700 hover:text-amber-800 shadow-md backdrop-blur-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportPDF(report.analysis);
                              }}
                            >
                              <Download className="h-4.5 w-4.5" />
                            </Button>

                            {/* View button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 rounded-full bg-amber-500/90 hover:bg-amber-500 text-white shadow-md backdrop-blur-sm"
                            >
                              <Eye className="h-4.5 w-4.5" />
                            </Button>
                          </div>
                        </div>

                        {/* Enhanced card content - Canva style */}
                        <CardContent className="p-4">
                          <div className="space-y-2">
                            {/* Pet name with indicator */}
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium text-lg text-amber-900 flex items-center gap-2">
                                {report.petName}
                                <div className="h-1.5 w-1.5 rounded-full bg-amber-400 hidden group-hover:block"></div>
                              </h3>
                              <div className="text-xs text-amber-600 bg-amber-100/50 px-2 py-1 rounded-full hidden group-hover:block">
                                Ver análisis
                              </div>
                            </div>
                            
                            {/* Context with better styling */}
                            <p className="text-sm text-gray-600 line-clamp-2 group-hover:text-amber-800 transition-colors duration-300">
                              {report.context || "Sin contexto adicional"}
                            </p>
                            
                            {/* Breed tag if available */}
                            {report.breed && (
                              <div className="flex items-center mt-2">
                                <div className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-100/50 flex items-center">
                                  <PawPrint className="h-3 w-3 mr-1 text-emerald-600" />
                                  {report.breed}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>

                        {/* Progress indicator - Netflix style */}
                        <div className="h-1 w-full bg-amber-100/50">
                          <div className="h-full bg-amber-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Enhanced pagination - YouTube style */}
              {!loading && !error && pagination.totalPages > 1 && (
                <div className="flex justify-center mt-8 pb-6 sm:pb-0">
                  <div className="flex gap-2 items-center bg-amber-50/50 px-4 py-2 rounded-full border border-amber-100/50 shadow-sm">
                    <Button
                      variant="ghost"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      size="sm"
                      className={cn(
                        "rounded-full h-8 px-3",
                        pagination.page === 1 
                          ? "opacity-50 cursor-not-allowed" 
                          : "hover:bg-amber-100/70 text-amber-700"
                      )}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Anterior
                    </Button>
                    
                    <div className="text-sm text-amber-700 px-2">
                      Página {pagination.page} de {pagination.totalPages}
                    </div>
                    
                    <Button
                      variant="ghost"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      size="sm"
                      className={cn(
                        "rounded-full h-8 px-3",
                        pagination.page === pagination.totalPages 
                          ? "opacity-50 cursor-not-allowed" 
                          : "hover:bg-amber-100/70 text-amber-700"
                      )}
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modales y overlays */}
        {isExporting && <PDFGenerationLoader progress={pdfProgress} />}
        <UpgradeModalReport
          isOpen={showUpgradeModalReport}
          onClose={() => setShowUpgradeModalReport(false)}
        />
        
        {/* Enhanced image preview - Netflix style */}
        <AnimatePresence mode="sync">
          {previewImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
              onClick={() => setPreviewImage(null)}
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25 }}
                className="max-w-3xl max-h-[90vh] p-4 relative"
              >
                <img
                  src={previewImage}
                  alt="Vista previa"
                  className="max-w-full max-h-full rounded-lg shadow-2xl"
                />
                <Button 
                  className="absolute top-2 right-2 rounded-full h-8 w-8 bg-black/50 hover:bg-black/70 text-white p-0"
                  onClick={() => setPreviewImage(null)}
                  size="sm"
                  variant="ghost"
                >
                  <X className="h-4 w-4" />
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
